import {
	autoUpdate,
	FloatingPortal,
	flip,
	offset,
	shift,
	size,
	useFloating,
} from "@floating-ui/react";
import { type FC, type MouseEvent, useEffect, useId, useRef } from "react";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";
import { AddressFormFieldControl } from "../../../api";
import { getConfig } from "../../../config";
import { cn } from "../../lib/utils";
import { Icon } from "../../primitives";
import { FormFieldError } from "../FormFieldError";
import { inputSize } from "./inputSize";

const PLACES_REQUEST_OPTIONS = {
	componentRestrictions: {
		country: ["us", "pr", "vi", "gu"],
	},
	types: ["street_address", "subpremise", "premise", "post_box"],
};

const FLOATING_MIDDLEWARE = [
	offset(5),
	flip(),
	shift(),
	size({
		apply({ rects, elements }) {
			elements.floating.style.minWidth = `${rects.reference.width}px`;
		},
	}),
];

const preventInputBlur = (event: MouseEvent) => {
	event.preventDefault();
};

export type AddressAutocompleteInputProps = {
	value: AddressFormFieldControl["value"];
	onChange?: AddressFormFieldControl["onChange"];
	onBlur?: AddressFormFieldControl["onBlur"];
	placeholder?: string;
	disabled?: boolean;
	readonly?: boolean;
	className?: string;
	compact?: boolean;
	error?: AddressFormFieldControl["error"];
	// When set, suggestions are portaled and floated relative to the input so they
	// aren't clipped by scrollable/overflow-hidden ancestors.
	anchor?: "bottom start";
};

export const AddressAutocompleteInput: FC<AddressAutocompleteInputProps> = ({
	value,
	onChange,
	onBlur,
	placeholder,
	className,
	compact,
	error,
	disabled,
	readonly,
	anchor,
}) => {
	const { frame, gutter, paddingX, paddingY } = inputSize(compact);
	const suggestionListId = useId();
	const {
		ready,
		value: autocompleteValue,
		suggestions: { status, data },
		setValue: setAutocompleteValue,
		clearSuggestions,
	} = usePlacesAutocomplete({
		defaultValue: value,
		debounce: 200,
		requestOptions: PLACES_REQUEST_OPTIONS,
	});

	const autocompleteValueRef = useRef(autocompleteValue);
	autocompleteValueRef.current = autocompleteValue;
	const pendingValidationValueRef = useRef<string | null>(null);

	useEffect(() => {
		const next = value ?? "";
		if (next !== autocompleteValueRef.current) {
			setAutocompleteValue(next, false);
		}
	}, [value, setAutocompleteValue]);

	useEffect(() => {
		if (
			pendingValidationValueRef.current !== null &&
			(value ?? "") === pendingValidationValueRef.current
		) {
			pendingValidationValueRef.current = null;
			onBlur?.();
		}
	}, [value, onBlur]);

	const showSuggestions = status === "OK" && data.length > 0;
	const { refs, floatingStyles } = useFloating({
		open: showSuggestions && !!anchor,
		placement: "bottom-start",
		middleware: FLOATING_MIDDLEWARE,
		whileElementsMounted: autoUpdate,
	});

	const handleInputChange = (next: string) => {
		setAutocompleteValue(next);
		onChange?.(next);
	};

	const handleSelect = (description: string) => {
		clearSuggestions();
		getGeocode({ address: description }).then((results) => {
			const { formatted_address } = results[0];
			let filteredAddress = formatted_address;
			if (getConfig().environment === "local") {
				filteredAddress = formatted_address.replace(/, USA$/, "");
			}
			pendingValidationValueRef.current = filteredAddress;
			setAutocompleteValue(filteredAddress, false);
			onChange?.(filteredAddress);
		});
	};

	const suggestionList = showSuggestions ? (
		<div
			id={suggestionListId}
			role="listbox"
			ref={refs.setFloating}
			style={anchor ? floatingStyles : undefined}
			onMouseDown={preventInputBlur}
			className={cn(
				"w-fit px-0 text-gray-700 font-normal rounded bg-white shadow-lg cursor-pointer select-none outline outline-[1px] outline-gray-100 outline-offset-[-1px]",
				anchor ? "z-[200001]" : "absolute min-w-full top-full mt-[5px] z-[10]",
			)}
		>
			{data.map(
				({
					place_id: placeId,
					description,
					structured_formatting: { main_text: mainText, secondary_text: secondaryText },
				}) => (
					<div
						key={placeId}
						role="option"
						className="flex items-center text-gray-700 w-full py-[9px] px-[10px] space-x-[5px] hover:bg-gray-100"
						onClick={() => handleSelect(description)}
					>
						<span className="font-medium">{mainText}</span>
						<span className="text-tertiary font-normal text-[15px]">{secondaryText}</span>
					</div>
				),
			)}
		</div>
	) : null;

	return (
		<div
			ref={refs.setReference}
			className={cn(
				"relative flex w-full rounded-md outline outline-[1px] outline-offset-[-1px] text-primary outline-gray-300 bg-gray-50 bg-opacity-70 placeholder:text-tertiary/85 focus-within:outline-[2px] focus-within:outline-gray-600 disabled:cursor-not-allowed",
				frame,
				disabled ? "cursor-not-allowed opacity-50" : "hover:outline-gray-500",
				className,
				readonly ? "bg-gray-200/70 outline-[0px] hover:outline-gray-300" : undefined,
				error
					? "outline-danger focus-within:outline-danger focus-within:hover:outline-danger hover:outline-danger hover:outline-2"
					: undefined,
			)}
		>
			<div
				className={cn(
					"h-full flex items-center justify-center rounded-l-md text-gray-700 bg-gray-100 border-r border-r-gray-300",
					gutter,
					readonly ? "bg-transparent mr-[-12px] border-r-0" : undefined,
				)}
			>
				<Icon type="map-pin" size="mini" />
			</div>
			<input
				value={autocompleteValue || ""}
				tabIndex={0}
				role="combobox"
				aria-autocomplete="list"
				aria-expanded={showSuggestions}
				aria-controls={suggestionListId}
				onChange={(event) => handleInputChange(event.target.value)}
				onBlur={() => {
					clearSuggestions();
					onBlur?.();
				}}
				disabled={!ready || readonly || disabled}
				placeholder={placeholder}
				autoComplete="one-time-code"
				data-1p-ignore
				data-lpignore
				className={cn(
					"w-full outline-none bg-transparent",
					paddingX,
					paddingY,
					readonly ? "text-primary" : undefined,
				)}
			/>
			{anchor && suggestionList ? (
				<FloatingPortal>{suggestionList}</FloatingPortal>
			) : (
				suggestionList
			)}
			<FormFieldError error={error} className={compact ? "left-[26px]" : "left-[42px]"} />
		</div>
	);
};
